import { MouseEvent, TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_DOUBLE_CLICK_POS_THRESHOLD, EDITOR_TOOLS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { useEditorContext } from '@editor/contexts/Editor'
import { hashString } from '@editor/hooks/useHashUpdater'
import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle, PolylineProps } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra, CanvasProps } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { SubtoolVertex } from '@editor/interfaces/tool'
import useConfigProps from '@editor/pages/projects/editor/hooks/useConfigProps'
import { useFocusItem } from '@editor/pages/projects/editor/hooks/useFocusItem'
import useUnlabeledColor from '@editor/pages/projects/editor/hooks/useUnlabeledColor'
import { isElementHovered } from '@editor/pages/projects/editor/infoPanels/utils'
import {
  selectIsAdjustingLabelColors,
  selectIsAdjustingUnlabeledColors,
  selectisGroupColoring,
} from '@editor/pages/projects/editor/store/selectors'
import useMergingPolyline from '@editor/pages/projects/editor/tools/Polyline/hooks/Polyline/useMergingPolyline'
import { toInspectionGroups } from '@editor/services/InspectionGroup'
import { isPointInsidePolygon } from '@editor/services/Points'
import { calculatePolylineLength } from '@editor/services/Polyline'
import { alignToZAxis } from '@editor/services/Shape'
import { getDistanceLabel, zeroPad } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import {
  selectConnectedAreas,
  selectInspectionItems,
  useToolUtilSelector,
  useVertexSelector,
} from '@editor/store/selectors'
import { setCursor } from '@editor/store/tool'
import { setDisableSwitchingVertexSubtool } from '@editor/store/toolUtils/vertex'
import { CircleAnchorIcon } from '@infraloop/common'
import { ThreeEvent } from '@react-three/fiber'
import * as Sentry from '@sentry/react'
import { Vector2, Vector3 } from 'three'

import { usePolylineSelector } from '../../../store/selector'
import { DISTANCE_LABEL_COMPLETED, DISTANCE_LABEL_STYLES, DISTANCE_LABEL_WORKING_COMPLETING } from '../../lineStyles'
import { PolylineToolMode } from '../../store/core'
import {
  addCompletedPolyline,
  addWorkingPoint,
  removeWorkingPolyline,
  setWorkingCrackDetectionId,
  undoLastWorkingPolyline,
  updateCompletedPolyline,
  upsertEditedPolyline,
} from '../../store/polyline'
import useSplitPolyline from './useSplitPolyline'

const tempPoint1 = new Vector3()
const tempPoint2 = new Vector3()
const tempSelectedPoint = new Vector3()

const useMainCanvas = (props: CanvasProps): CanvasConfig => {
  const { showLabels } = useConfigProps(props)
  const focusToItem = useFocusItem()

  // Context
  const { cameraRef } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const connectedAreas = useAppSelector(selectConnectedAreas)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)
  const disableTools = useToolUtilSelector((state) => state.disableTools)
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)

  const isAdjustingUnlabeledColors = useAppSelector(selectIsAdjustingUnlabeledColors)
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const isGroupColoring = useAppSelector(selectisGroupColoring)
  const polylineDisplaySettings = useAppSelector((state) => state.editorSettings.display.polyline)
  const getUnlabeledColor = useUnlabeledColor()
  const currentAdjustingKey = useAppSelector((state) => state.toolUtil.core.currentAdjustingKey)

  const inspectionGroupsPanelItems = useAppSelector((state) => state.inspectionGroup.groups)
  const inspectionGroups = toInspectionGroups(inspectionGroupsPanelItems)

  const mode = usePolylineSelector((state) => state.core.mode)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)
  const savingPolylines = usePolylineSelector((state) => state.polyline.savingPolylines)
  const workingWidth = usePolylineSelector((state) => state.polyline.workingWidth)
  const workingPoints = usePolylineSelector((state) => state.polyline.workingPoints)
  const inToolLabelVisibility = usePolylineSelector((state) => state.polyline.inToolLabelVisibility)

  const crackDetection = usePolylineSelector((state) => state.crackDetection.executions[state.crackDetection.areaId])

  // State
  const [isClosing, setIsClosing] = useState(false)

  // Refs
  const touchedPoint = useRef<PointArray | undefined>(undefined)
  const isUpTouchedHandled = useRef(false)

  // Flags
  const isToolSelected = selectedTool === EDITOR_TOOLS.POLYLINE

  const allSavedPolylines = useMemo(
    () => inspectionItems.filterByType(InspectionItemType.PolylineLength),
    [inspectionItems],
  )

  const savedPolylines = useMemo(
    () =>
      allSavedPolylines
        .filter((item) => {
          // Exclude polylines that have been edited to have zero points (marked for deletion)
          const editedPoly = editedPolylines.find((ep) => ep.id === item.inspection_item_id)
          if (editedPoly?.isDeleted) {
            return false
          }

          return true
        })
        .map<InspectionItem>((item) => {
          const editedPoly = editedPolylines.find((ep) => ep.id === item.inspection_item_id)

          return {
            ...item,
            polyline_length: {
              ...item.polyline_length,
              positions_for_distance: editedPoly?.points || item.polyline_length?.positions_for_distance,
            },
          }
        }),
    [allSavedPolylines, editedPolylines],
  )

  /**
   * Aligned crack detection polygon in 2D for crack detection area, if any.
   */
  const alignedCrackPolygonPoints2D = useMemo(() => {
    if (!crackDetection?.points.length) return null

    const crackDetectionPoints = crackDetection.points.map((pt) => new Vector3(...pt))
    const alignedPolygon = alignToZAxis(crackDetectionPoints)

    return {
      id: crackDetection.id,
      points: alignedPolygon.points.map((p) => new Vector2(p.x, p.y)),
      matrix: alignedPolygon.matrix,
    }
  }, [crackDetection])

  // Some more hooks!
  const {
    isMergingPolyline,
    handleMergePolylines,
    hoveredAnchorForMerging,
    selectedPolylineForMergingRef,
    setHoveredAnchorForMerging,
  } = useMergingPolyline(savedPolylines)
  const { hoveredPolylineIdRef, handleSplitPolyline } = useSplitPolyline(savedPolylines)

  /**
   * Reset the drawing states.
   */
  const resetDrawingLine = useCallback(() => {
    dispatch(removeWorkingPolyline())
    setIsClosing(false)
  }, [dispatch])

  /**
   * End drawing of polyline
   */
  const endPolyline = useCallback(
    (point?: Vector3) => {
      if (workingPoints.length < 2) return

      dispatch(
        addCompletedPolyline({
          id: crypto.randomUUID(),
          points: workingPoints.concat(point ? [point.toArray()] : []),
          width: workingWidth,
        }),
      )
      resetDrawingLine()
    },
    [workingPoints, workingWidth, resetDrawingLine, dispatch],
  )

  /**
   * Main action to draw the polyline.
   *
   * @param points Clicked point in 3D Space.
   * @param extra Extra events data.
   */
  const handleUp = useCallback(
    (
      me: MouseEvent<HTMLDivElement> | null,
      te: TouchEvent<HTMLDivElement> | null,
      point: PointArray | undefined,
      solidMeshPoint: PointArray | undefined,
      { isDragged, isDoubleClicked }: CanvasEventsExtra,
    ) => {
      const clickedPoint = solidMeshPoint || point
      if (isDragged || !isToolSelected || disableTools) return

      // If we're on RemoveVertex subtool and clicked on an edge (not a vertex), cut the polyline
      if (currentVertexSubtool === SubtoolVertex.RemoveVertex && hoveredPolylineIdRef.current) {
        if (clickedPoint) handleSplitPolyline(clickedPoint)
        return
      }

      // Only proceed if in AddVertex subtool
      if (currentVertexSubtool !== SubtoolVertex.AddVertex || hoveredPolylineIdRef.current) return

      // Figure out distance in screen-space between clicked point and first working point
      // Click point we can get from event but first working point we need to project to screen space
      const ev = me || te
      let isScreenSpaceClose = false
      if (workingPoints.length > 1 && ev) {
        const screenPoint = me
          ? { x: me.clientX, y: me.clientY }
          : { x: te!.changedTouches[0].clientX, y: te!.changedTouches[0].clientY }

        const canvasRect = (ev.target as HTMLDivElement).getBoundingClientRect()
        const closingPointNDC = new Vector3(...workingPoints.slice(-1)[0]).project(cameraRef.current!)
        const closingPointScreen = {
          x: ((closingPointNDC.x + 1) / 2) * canvasRect.width + canvasRect.left,
          y: ((-closingPointNDC.y + 1) / 2) * canvasRect.height + canvasRect.top,
        }
        const distanceToFirstPoint = Math.hypot(
          screenPoint.x - closingPointScreen.x,
          screenPoint.y - closingPointScreen.y,
        )
        isScreenSpaceClose = distanceToFirstPoint < EDITOR_DOUBLE_CLICK_POS_THRESHOLD
      }

      if (isClosing || isScreenSpaceClose || isDoubleClicked) {
        if (workingPoints.length >= 2) endPolyline()
        setIsClosing(false)
        return
      }

      if (clickedPoint) {
        if (alignedCrackPolygonPoints2D) {
          const alignedClickPoint = new Vector3(...clickedPoint).applyMatrix4(alignedCrackPolygonPoints2D.matrix)
          if (isPointInsidePolygon(new Vector2(...alignedClickPoint), alignedCrackPolygonPoints2D.points)) {
            dispatch(setWorkingCrackDetectionId(alignedCrackPolygonPoints2D.id))
          }
        }

        tempSelectedPoint.fromArray(clickedPoint)
        dispatch(addWorkingPoint(clickedPoint))
      }
    },
    [
      cameraRef,
      isToolSelected,
      workingPoints,
      isClosing,
      disableTools,
      currentVertexSubtool,
      hoveredPolylineIdRef,
      alignedCrackPolygonPoints2D,
      handleSplitPolyline,
      endPolyline,
      dispatch,
    ],
  )

  /**
   * Handle completion of the working polyline through button.
   */
  const onComplete = useCallback(() => {
    if (!isToolSelected || currentVertexSubtool !== SubtoolVertex.AddVertex || workingPoints.length < 2) return

    endPolyline()
  }, [isToolSelected, currentVertexSubtool, workingPoints.length, endPolyline])

  /**
   * Undo the last point of the working polyline.
   * If the last polyline is completed, it will remove the last point and make it the drawing stage.
   */
  const onUndo = useCallback(() => {
    if (!isToolSelected || currentVertexSubtool !== SubtoolVertex.AddVertex) return

    dispatch(undoLastWorkingPolyline())
    setIsClosing(false)
  }, [isToolSelected, currentVertexSubtool, dispatch])

  /**
   * Cancel the currently drawing polyline.
   */
  const onCancel = useCallback(() => {
    if (!isToolSelected || currentVertexSubtool !== SubtoolVertex.AddVertex) return

    resetDrawingLine()
  }, [isToolSelected, currentVertexSubtool, resetDrawingLine])

  /**
   * Focus on a polyline.
   *
   * @param hashedItemId Hashed ID of the inspection item to focus on
   */
  const onFocusItem = useCallback(
    async (hashedItemId: string) => {
      try {
        // Get the inspection item and make sure it's a polyline item
        const item = allSavedPolylines.find((it) => hashString(it.inspection_item_id!) === hashedItemId)
        if (!item?.polyline_length?.positions_for_distance) return // no need to throw error, it can be a valid call from URL deep-link broadcasted to all tools

        await focusToItem(item.polyline_length.positions_for_distance, {
          customProfile: item.camera_profile || undefined,
        })
      } catch (error) {
        Sentry.captureException(error, { tags: { location: 'Polyline/useMainCanvas/onFocusItem' } })
      }
    },
    [allSavedPolylines, focusToItem],
  )

  /**
   * On leaving AddVertex subtool, reset the drawing state.
   */
  useEffect(() => {
    if (!isToolSelected || currentVertexSubtool === SubtoolVertex.AddVertex) return

    resetDrawingLine()
  }, [isToolSelected, currentVertexSubtool, resetDrawingLine])

  /**
   * When there's working points, disable switching vertex subtools to avoid confusion.
   */
  useEffect(() => {
    dispatch(setDisableSwitchingVertexSubtool(workingPoints.length > 0))
  }, [workingPoints, dispatch])

  return {
    objects: {
      circleAnchors: [
        ...useMemo(() => {
          if (!isToolSelected || workingPoints.length < 1) return []

          return [
            {
              id: 'working-polyline-first-anchor',
              point: workingPoints[0] || [0, 0, 0],
              color: isClosing ? '#f00' : '#333',
              depthTest: false,
              depthWrite: false,
              renderOrder: 9999,
              isInteractable: false,
              scale: 1.3,
              onEnter: (event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation()
                dispatch(setCursor(CursorState.POINTER))
                setIsClosing(true)
              },
              onLeave: (event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation()
                dispatch(setCursor(CursorState.NONE))
                setIsClosing(false)
              },
            },
            /**
             * THE anchor to click to close the polyline.
             * It should be shown on the last point of workingPoints but this is only a placeholder
             * for direct manipulation.
             */
            {
              id: 'working-polyline-complete-anchor',
              point: workingPoints.slice(-1)[0] || [0, 0, 0],
              outlineOpacity: 0,
              color: isClosing ? '#f00' : '#248CE4',
              depthTest: false,
              depthWrite: false,
              renderOrder: 9999,
              icon: CircleAnchorIcon.CHECK,
              iconScale: 1.6,
              scale: 1.7,
              hoverScale: 1.5,
              iconColor: 'white',
              tooltipText: isClosing ? 'この延長の作成を完了' : undefined,
              visible: workingPoints.length >= 2,
              onEnter: (event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation()
                dispatch(setCursor(CursorState.POINTER))
                setIsClosing(true)
              },
              onLeave: (event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation()
                dispatch(setCursor(CursorState.NONE))
                setIsClosing(false)
              },
            },
          ]
        }, [workingPoints, isClosing, isToolSelected, dispatch]),
      ],

      polylines: [
        /**
         * Working polyline.
         * - The polyline that is being drawn but not yet completed.
         */
        ...useMemo((): PolylineProps[] => {
          if (!isToolSelected || !workingPoints.length) return []

          let style = DISTANCE_LABEL_STYLES
          if (isClosing) {
            style = DISTANCE_LABEL_WORKING_COMPLETING
          }

          return [
            {
              ...style,
              id: 'working-polyline-drawing-outline',
              points: workingPoints,
              hideBottomAnchor: true,
              isWorking: true,
              isInteractable: false,
              visible: workingPoints.length > 0,
              label: inToolLabelVisibility.primary
                ? {
                    label: getDistanceLabel(calculatePolylineLength(workingPoints)),
                    generateLabel: (pts: PointArray[]): string => getDistanceLabel(calculatePolylineLength(pts)),
                    styles: {
                      backgroundColor: '#88DF41',
                      opacity: 1,
                    },
                  }
                : undefined,
              segmentLabels: inToolLabelVisibility.edge
                ? {
                    generateLabel: (pts: PointArray[]): string => getDistanceLabel(calculatePolylineLength(pts)),
                    styles: {
                      color: polylineDisplaySettings.edge.label.text_color,
                      backgroundColor: polylineDisplaySettings.edge.label.bg_color,
                    },
                  }
                : undefined,
            },
          ]
        }, [workingPoints, isClosing, isToolSelected, polylineDisplaySettings, inToolLabelVisibility]),

        /**
         * Polyline between 2 polylines to be merged.
         * This shows a temporary polyline between the first selected polyline and the hovered anchor of the second polyline.
         */
        ...useMemo(
          () =>
            hoveredAnchorForMerging?.length === 2
              ? [
                  {
                    id: 'merging-polyline-preview',
                    points: hoveredAnchorForMerging,
                    isInteractable: false,
                    line: {
                      style: LineStyle.Dashed,
                      color: '#FAF089',
                      thickness: 0.002,
                    },
                    lineOutline: {
                      visible: false,
                    },
                  },
                ]
              : [],
          [hoveredAnchorForMerging],
        ),

        /**
         * Completed polyline.
         * - Working polyline that has been completed but not yet saved to server.
         * - Also covers polyline that is being saved to server.
         */
        ...useMemo(
          () =>
            [...savingPolylines, ...completedPolylines].map<PolylineProps>(({ id, points, width }, index) => {
              const isLineInteractable = [SubtoolVertex.AddVertex, SubtoolVertex.RemoveVertex].includes(
                currentVertexSubtool,
              )
              const isHovered = hoveredElementId === id
              const isSelected = selectedElementIds.includes(id)
              const isHoverSelected = isHovered || isSelected
              const isSavingPolyline = savingPolylines.some((pl) => pl.id === id)

              return {
                ...DISTANCE_LABEL_COMPLETED,
                id,
                points,
                lineStyle: LineStyle.Dashed,
                isEditing: editingElementId === id,
                isInteractable: isLineInteractable,
                visible: !hiddenElementIds.includes(id) && (isToolSelected || isSavingPolyline),
                label: inToolLabelVisibility.primary
                  ? {
                      label: `延長${zeroPad(index + allSavedPolylines.length + 1, 3)}: ${getDistanceLabel(calculatePolylineLength(points), width)}`,
                      generateLabel: (pts: PointArray[]): string =>
                        `延長${zeroPad(index + allSavedPolylines.length + 1, 3)}: ${getDistanceLabel(calculatePolylineLength(pts), width)}`,
                      styles: {
                        backgroundColor: '#88DF41',
                        opacity: mode === PolylineToolMode.Draw ? 1 : 0,
                        border:
                          isHoverSelected && isToolSelected ? `4px solid ${isHovered ? 'yellow' : 'white'}` : undefined,
                      },
                    }
                  : undefined,
                segmentLabels: inToolLabelVisibility.edge
                  ? {
                      generateLabel: (pts: PointArray[]): string => getDistanceLabel(calculatePolylineLength(pts)),
                      styles: {
                        color: polylineDisplaySettings.edge.label.text_color,
                        backgroundColor: polylineDisplaySettings.edge.label.bg_color,
                        border:
                          isHoverSelected && isToolSelected ? `4px solid ${isHovered ? 'yellow' : 'white'}` : undefined,
                      },
                      isAdjustingColors:
                        isAdjustingLabelColors &&
                        currentAdjustingKey !== undefined &&
                        ['polyline.edge.label.text_color', 'polyline.edge.label.bg_color'].includes(
                          currentAdjustingKey,
                        ),
                    }
                  : undefined,
                vertexAnchors: {
                  isInteractable: true,
                  visible: mode === PolylineToolMode.Draw && isToolSelected && !isSavingPolyline,
                  depthTest: false,
                  depthWrite: false,
                  iconScale: 1.6,
                  onUpdated: (positions: PointArray[]) => {
                    let crackDetectionId: string | undefined
                    if (alignedCrackPolygonPoints2D) {
                      const alignedWorkingPoints = positions.map((pt) =>
                        new Vector3(...pt).applyMatrix4(alignedCrackPolygonPoints2D.matrix),
                      )
                      if (
                        alignedWorkingPoints.some((pt) =>
                          isPointInsidePolygon(new Vector2(...pt), alignedCrackPolygonPoints2D.points),
                        )
                      ) {
                        crackDetectionId = alignedCrackPolygonPoints2D.id
                      }
                    }

                    dispatch(
                      updateCompletedPolyline({
                        id,
                        points: positions,
                        width,
                        jobId: crackDetectionId,
                      }),
                    )
                  },
                  onEnter: (idx) => {
                    if (isMergingPolyline && selectedPolylineForMergingRef.current?.id !== id && points[idx]) {
                      setHoveredAnchorForMerging((prev) => {
                        // This only for the second point. If the first point is not set, ignore.
                        if (!prev) return prev
                        return [prev[0], points[idx]]
                      })
                    }
                  },
                  onLeave: () => {
                    if (isMergingPolyline && selectedPolylineForMergingRef.current?.id !== id) {
                      setHoveredAnchorForMerging((prev) => {
                        // This only for the second point. If the first point is not set, ignore.
                        if (!prev) return prev
                        return [prev[0]]
                      })
                    }
                  },
                  onUp(anchorIndex) {
                    if (isMergingPolyline && points[anchorIndex]) {
                      handleMergePolylines(id, anchorIndex, points[anchorIndex])
                    }
                  },
                },
                onEnter: () => {
                  hoveredPolylineIdRef.current = id
                },
                onLeave: () => {
                  hoveredPolylineIdRef.current = undefined
                },
              }
            }),
          [
            isToolSelected,
            isMergingPolyline,
            savingPolylines,
            completedPolylines,
            allSavedPolylines.length,
            hoveredElementId,
            selectedElementIds,
            editingElementId,
            hiddenElementIds,
            mode,
            currentVertexSubtool,
            hoveredPolylineIdRef,
            selectedPolylineForMergingRef,
            alignedCrackPolygonPoints2D,
            isAdjustingLabelColors,
            currentAdjustingKey,
            polylineDisplaySettings,
            inToolLabelVisibility,
            setHoveredAnchorForMerging,
            handleMergePolylines,
            dispatch,
          ],
        ),

        /**
         * Saved polyline.
         * - This is the polyline that has been retrieved from the server.
         */
        ...useMemo(
          () =>
            savedPolylines.map<PolylineProps | null>((inspectionItem) => {
              const { part_name, inspection_item_id, inspection_group_id, polyline_length } = inspectionItem
              const points = polyline_length?.positions_for_distance
              if (!points) return null

              const id = inspection_item_id!
              const isLineInteractable =
                [SubtoolVertex.AddVertex, SubtoolVertex.RemoveVertex].includes(currentVertexSubtool) || !isToolSelected

              // -- Find associated width item
              let polylineWidth: number | undefined
              const editedPolyline = editedPolylines.find((ep) => ep.id === inspection_item_id)

              // If the polyline has been edited, use the width from the edited data
              // (even if the points have been edited to be empty, which means deletion)
              if (editedPolyline) {
                polylineWidth = editedPolyline.width
              } else {
                // If no edited data, find from inspection items
                const widthItem = inspectionItems.items.find(
                  (item) =>
                    item.item_type === InspectionItemType.PolylineWidth &&
                    item.polyline_length_id === inspection_item_id,
                )
                polylineWidth = widthItem?.polyline_width?.estimated_value ?? undefined
              }

              const isHovered = isElementHovered(inspectionItem, hoveredElementId, inspectionGroups)
              const isSelected = selectedElementIds.includes(id)
              const isHoverSelected = isHovered || isSelected
              const group = inspectionGroups.find((grp) => grp && inspection_group_id === grp.group_id)
              const parentArea = connectedAreas?.find(
                (area) => area.current.area.inspection_area_id === inspectionItem.inspection_area_id,
              )

              const style = {
                ...DISTANCE_LABEL_STYLES,
                line: {
                  ...DISTANCE_LABEL_STYLES.line,
                  color: isGroupColoring
                    ? group?.color || getUnlabeledColor(inspectionItem.inspection_area_id)
                    : polylineDisplaySettings.color,
                },
                lineOutline: {
                  ...DISTANCE_LABEL_STYLES.lineOutline,
                  color: 'transparent',
                  thickness: 0.006,
                },
              }

              if (isHovered || selectedElementIds.includes(id)) {
                style.lineOutline.color = isHovered ? 'yellow' : 'white'
                style.lineOutline.visible = !isToolSelected
              }

              // Go through each section of the polyline and calculate the distance between them, then the total distance.
              const totalDistance = points.reduce<number>((collection, point, index) => {
                const pointsPair = [point, points[index + 1]]
                if (!pointsPair[1]) return collection
                return collection + tempPoint1.fromArray(pointsPair[0]).distanceTo(tempPoint2.fromArray(pointsPair[1]))
              }, 0)

              const showPrimaryLabel = isToolSelected
                ? inToolLabelVisibility.primary
                : polylineDisplaySettings.primary.label.visible
              const showEdgeLabel = isToolSelected
                ? inToolLabelVisibility.edge
                : polylineDisplaySettings.edge.label.visible

              return {
                ...style,
                id,
                points,
                offsetTransformation: parentArea?.matrix,
                isEditing: editingElementId === id && isToolSelected,
                isInteractable: isLineInteractable,
                isAdjustingColors:
                  (isAdjustingUnlabeledColors && isGroupColoring) ||
                  (!isGroupColoring &&
                    currentAdjustingKey !== undefined &&
                    [`polyline.color`].includes(currentAdjustingKey)),
                opacity: !hiddenElementIds.includes(id) || isHovered ? 1 : 0,
                label: showPrimaryLabel
                  ? {
                      label: `${part_name}: ${getDistanceLabel(totalDistance, polylineWidth)}`,
                      generateLabel: (pts: PointArray[]): string =>
                        `${part_name}: ${getDistanceLabel(calculatePolylineLength(pts), polylineWidth)}`,
                      styles: {
                        color: polylineDisplaySettings.primary.label.text_color,
                        backgroundColor: polylineDisplaySettings.primary.label.bg_color,
                        border:
                          isHoverSelected && isToolSelected ? `4px solid ${isHovered ? 'yellow' : 'white'}` : undefined,
                      },
                      visible: showLabels && (selectedElementIds.includes(id) || isHovered || isToolSelected),
                      isAdjustingColors:
                        isAdjustingLabelColors &&
                        currentAdjustingKey !== undefined &&
                        ['polyline.primary.label.text_color', 'polyline.primary.label.bg_color'].includes(
                          currentAdjustingKey,
                        ),
                    }
                  : undefined,
                segmentLabels: showEdgeLabel
                  ? {
                      generateLabel: (pts: PointArray[]): string => getDistanceLabel(calculatePolylineLength(pts)),
                      styles: {
                        color: polylineDisplaySettings.edge.label.text_color,
                        backgroundColor: polylineDisplaySettings.edge.label.bg_color,
                        border:
                          isHoverSelected && isToolSelected ? `4px solid ${isHovered ? 'yellow' : 'white'}` : undefined,
                      },
                      visible: showLabels && (selectedElementIds.includes(id) || isHovered || isToolSelected),
                      isAdjustingColors:
                        isAdjustingLabelColors &&
                        currentAdjustingKey !== undefined &&
                        ['polyline.edge.label.text_color', 'polyline.edge.label.bg_color'].includes(
                          currentAdjustingKey,
                        ),
                    }
                  : undefined,
                vertexAnchors: {
                  isInteractable: true,
                  visible:
                    mode === PolylineToolMode.Draw && isToolSelected && !savingPolylines.find((pl) => pl.id === id),
                  depthTest: false,
                  depthWrite: false,
                  onUpdated: isToolSelected
                    ? (positions: PointArray[]) => {
                        dispatch(
                          upsertEditedPolyline({
                            id,
                            points: positions,
                          }),
                        )
                      }
                    : undefined,
                  onEnter: (idx) => {
                    if (isMergingPolyline && selectedPolylineForMergingRef.current?.id !== id && points[idx]) {
                      setHoveredAnchorForMerging((prev) => {
                        // This only for the second point. If the first point is not set, ignore.
                        if (!prev) return prev
                        return [prev[0], points[idx]]
                      })
                    }
                  },
                  onLeave: () => {
                    if (isMergingPolyline && selectedPolylineForMergingRef.current?.id !== id) {
                      setHoveredAnchorForMerging((prev) => {
                        // This only for the second point. If the first point is not set, ignore.
                        if (!prev) return prev
                        return [prev[0]]
                      })
                    }
                  },
                  onUp(anchorIndex) {
                    if (isMergingPolyline && points[anchorIndex]) {
                      handleMergePolylines(id, anchorIndex, points[anchorIndex])
                    }
                  },
                },
                onEnter: () => {
                  hoveredPolylineIdRef.current = id
                },
                onLeave: () => {
                  hoveredPolylineIdRef.current = undefined
                },
              }
            }),
          [
            showLabels,
            savedPolylines,
            editedPolylines,
            inspectionGroups,
            editingElementId,
            hoveredElementId,
            hiddenElementIds,
            selectedElementIds,
            inspectionItems,
            connectedAreas,
            isToolSelected,
            mode,
            savingPolylines,
            currentVertexSubtool,
            isMergingPolyline,
            hoveredPolylineIdRef,
            selectedPolylineForMergingRef,
            currentAdjustingKey,
            isGroupColoring,
            isAdjustingUnlabeledColors,
            isAdjustingLabelColors,
            polylineDisplaySettings,
            inToolLabelVisibility,
            getUnlabeledColor,
            setHoveredAnchorForMerging,
            handleMergePolylines,
            dispatch,
          ],
        ),
      ].filter(Boolean) as PolylineProps[],
    },
    events: {
      onTouchStart(ev, point, solidMeshPoint) {
        touchedPoint.current = point || solidMeshPoint
      },
      onTouchEnd(ev, extras) {
        handleUp(null, ev, touchedPoint.current, touchedPoint.current, extras)
        isUpTouchedHandled.current = true
        touchedPoint.current = undefined
      },
      onMouseUp(ev, point, solidMeshPoint, extras) {
        if (isUpTouchedHandled.current) {
          isUpTouchedHandled.current = false
          return
        }

        handleUp(ev, null, point, solidMeshPoint, extras)
      },
      onUndo,
      onCancel,
      onComplete,
      onFocusItem,
    },
  }
}

export default useMainCanvas
